import { useState, useEffect, useContext } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useToast } from "../design-system/contexts/ToastContext";
import { useLiveQuery } from "dexie-react-hooks";
import AuthContext from "../contexts/AuthContext";
import { useTheme, Avatar } from "@/design-system";
import {
  listEvents,
  downloadLeads,
  getEventDetailsBySponsorCode,
  createUserSponsorMapping,
} from "../common/api";
import { events, leads } from "./../common/db";
import { AddedEventDisplayModal, InvalidSponsorCodeModal } from "./../common/modal";
import { addAllEventsToIndexDB } from "./../common/functions";
import { EventCard } from "../components/EventCard";
import { AppHeader } from "../components/AppLayout";
import ProfileModal from "../components/ProfileModal";
// iOS keyboard is now handled via CSS dvh units and viewport meta tag
import "../components/AppLayout/AppLayout.css";
import "./Home/Home.css";

const AllEvents = () => {
  // Get all data from IndexedDB
  const allEventItems = useLiveQuery(async () => {
    let indexedDBEvents = await events.toArray();
    return indexedDBEvents.reverse();
  });

  const [addSponsorCodeModal, setAddSponsorCodeModal] = useState(false);
  const [sponsorCode, setSponsorCode] = useState();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [eventStorageData, setEventStorageData] = useState(null);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [index, setIndex] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [eventSponsorCode, setEventSponsorCode] = useState();
  const [addedEventModal, setAddedEventModal] = useState(false);
  const [addedEventDatails, setAddedEventDatails] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [invalidCodeModalOpen, setInvalidCodeModalOpen] = useState(false);

  const navigate = useNavigate();
  const Context = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const { success, error: showError } = useToast();

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show API error from context if present
  useEffect(() => {
    if (Context.apiError) {
      showError(Context.apiError);
    }
  }, [Context.apiError]);

  useEffect(() => {
    if (localStorage.getItem("sponsorCodePreFill")) {
      let sponsorCodeFromLocalStorage = localStorage.getItem("sponsorCodePreFill");
      setSponsorCode(sponsorCodeFromLocalStorage);
      if (Array.isArray(Context.eventUpdate) && Context.eventUpdate.length > 0) {
        let filterDesiredEvent = Context.eventUpdate.filter(
          (event) => event.sponsorCode === sponsorCodeFromLocalStorage
        );
        if (filterDesiredEvent.length === 0) {
          // Event not in list, add it
          addSponsorCodeAutomatically(sponsorCodeFromLocalStorage);
        } else {
          // Event already exists, navigate directly to it
          const existingEvent = filterDesiredEvent[0];
          navigate({
            pathname: `/events/${existingEvent.eventId}/scanDetails`,
            search: `?sponsor=${existingEvent.sponsorCode}`,
          });
        }
        localStorage.removeItem("sponsorCodePreFill");
      } else if (Context.eventUpdate === false) {
        // Context not yet loaded, wait for it
        return;
      } else {
        addSponsorCodeAutomatically(sponsorCodeFromLocalStorage);
        localStorage.removeItem("sponsorCodePreFill");
      }
    }

    const handleOnline = () => setNetworkStatus(true);
    const handleOffline = () => setNetworkStatus(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [Context.eventUpdate]);

  useEffect(() => {
    if (networkStatus) {
      listAllEvents();
      leads.clear();
    } else {
      offlineFunction();
    }
  }, [networkStatus]);

  // Sync eventStorageData with allEventItems on initial load (only when not searching)
  // This handles the case when page loads with data from IndexedDB
  useEffect(() => {
    if (allEventItems && !searchValue && eventStorageData === null) {
      setEventStorageData(allEventItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEventItems, searchValue]);

  let offlineFunction = async () => {
    let offlineData = await events.toArray();
    setEventStorageData(offlineData.reverse());
  };

  // GraphQL for downloads
  const Download = async (sponsorCode, eventId) => {
    setLoading(sponsorCode);
    try {
      const res = await downloadLeads(sponsorCode, eventId);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in downloadLeads:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to download leads";
        showError(errorMessage);
        setLoading(false);
        return;
      }

      let allLeads = JSON.parse(res.data.downloadSponsorLeads);
      if (allLeads.statusCode == 200) {
        setLoading(false);
        success("Downloading file");
        window.location.assign(allLeads.body.downloadUrl);
      } else {
        setLoading(false);
        const errorMsg = allLeads.body || "Something went wrong!";
        showError(errorMsg);
      }
    } catch (err) {
      console.error("Error in downloadLeads:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to download leads";
      showError(errorMessage);
      setLoading(false);
    }
  };

  // GraphQL API for fetching all event details corresponding to account
  const listAllEvents = async () => {
    try {
      const res = await listEvents();

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in listEvents:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to load events";
        showError(errorMessage);
        setEventStorageData([]);
        return;
      }

      let allEventDetails = JSON.parse(res.data.listEventsForSponsor);
      if (allEventDetails.statusCode == 200) {
        events.clear();
        let allEvents = allEventDetails.body;
        addAllEventsToIndexDB(allEvents);
        setEventStorageData(allEvents);
        const profileName =
          Context.state.user.attributes.name ?? "reachus@konfhub.com";
        const profileEmail =
          Context.state.user.attributes.email ?? "reachus@konfhub.com";
        localStorage.setItem("profileName", JSON.stringify(profileName));
        localStorage.setItem("profileEmail", JSON.stringify(profileEmail));
      } else {
        // Handle API error - show toast and set empty data to render UI
        const errorMessage = allEventDetails.body || "Error loading events";
        showError(errorMessage);
        setEventStorageData([]);
      }
    } catch (err) {
      console.error("Error in listEvents:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to load events. Please try again.";
      showError(errorMessage);
      setEventStorageData([]);
    }
  };

  // Search functionality
  const filteringData = (value) => {
    setSearchValue(value);
    if (value && allEventItems) {
      // Filter events while preserving all properties including eventPoster
      let dataFiltered = allEventItems.filter((dataItems) => {
        return dataItems.eventName.toLowerCase().includes(value.toLowerCase());
      });
      setEventStorageData(dataFiltered);
    } else if (allEventItems) {
      // Clear search - restore all events with all their properties
      setEventStorageData(allEventItems);
    }
  };

  const mappingEvents = (eventId) => {
    let booleanValue = allEventItems.map((value) => {
      if (value.eventId == eventId) return true;
      else return false;
    });
    if (booleanValue.includes(true)) return true;
    else return false;
  };

  const addSponsorCodeAutomatically = async (sponsorCodeThroughUrl) => {
    await getEventDetailsBySponsorCode(sponsorCodeThroughUrl)
      .then(async (res) => {
        let eventDetails = JSON.parse(res.data.getEventDetailsBySponsorCode);
        if (eventDetails.statusCode === 200) {
          setError("");
          const eventData = eventDetails.body[0];
          try {
            await sendEventDetailsToApi(
              eventData.sponsorCode,
              eventData.eventId
            );
            listEvents()
              .then((res) => {
                let allEventDetails = JSON.parse(res.data.listEventsForSponsor);
                if (allEventDetails.statusCode == 200) {
                  events.clear();
                  let allEvents = allEventDetails.body;
                  addAllEventsToIndexDB(allEvents);
                  // Navigate directly to the event (from QR code flow)
                  navigate({
                    pathname: `/events/${eventData.eventId}/scanDetails`,
                    search: `?sponsor=${eventData.sponsorCode}`,
                  });
                }
              })
              .catch((err) => console.log(err));
            setLoading(false);
          } catch (err) {
            console.log(err);
          }
          let eventsList = await events.toArray();
          setEventStorageData(eventsList.reverse());
        } else {
          setLoading(false);
          setAddSponsorCodeModal(true);
          setSponsorCode(sponsorCodeThroughUrl);
          navigate({ pathname: "/events/EnterSponsorCode" });
        }
      })
      .catch((err) => console.log(err));
  };

  // GraphQL API for fetching event details using sponsor code
  const submitClickHandler = async () => {
    await getEventDetailsBySponsorCode(sponsorCode)
      .then(async (res) => {
        let eventDetails = JSON.parse(res.data.getEventDetailsBySponsorCode);
        if (eventDetails.statusCode === 200) {
          setAddedEventDatails(eventDetails.body[0]);
          setError("");
          setLoading(false);

          if (mappingEvents(eventDetails.body[0].eventId)) {
            setError("Event is already present");
          } else {
            setError("");
            setAddSponsorCodeModal(false);
            setAddedEventModal(true);

            try {
              await sendEventDetailsToApi(
                eventDetails.body[0].sponsorCode,
                eventDetails.body[0].eventId
              );
              listAllEvents();
              setLoading(false);
            } catch (err) {
              console.log(err);
            }
            let eventsList = await events.toArray();
            setEventStorageData(eventsList.reverse());
          }
        } else {
          setLoading(false);
          if (
            eventDetails.body.includes(
              "sponsorCode does not meet minimum length of 6"
            )
          ) {
            setError("Please enter a valid sponsor code");
          } else {
            setError(JSON.parse(eventDetails.body));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const sendEventDetailsToApi = async (sponsorCode, eventId) => {
    setLoading(true);
    const profileUserId = Context.state.user.attributes["custom:userId"];
    let event_details = {
      userId: profileUserId,
      eventId: eventId,
      sponsorCode: sponsorCode,
    };
    try {
      const res = await createUserSponsorMapping(event_details);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in createUserSponsorMapping:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to add event";
        showError(errorMessage);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Error in createUserSponsorMapping:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to add event";
      showError(errorMessage);
      setLoading(false);
    }
  };

  // Date and time conversion to display on event card
  const DateTimeConversion = (date, time) => {
    let dateTime = date.split("T")[0] + "T" + time + "Z";
    let dateIST = new Date(dateTime);
    let datesplit = dateIST.toDateString().split(" ");
    let finalDate = datesplit[1] + " " + datesplit[2] + ", " + datesplit[3];

    let [hourString, minute] = dateIST.toTimeString().split(":");
    let hour = +hourString % 24;
    let convertedTime =
      (hour % 12 || 12) + ":" + minute + (hour < 12 ? " AM " : " PM ") + "IST";

    return finalDate + " " + convertedTime;
  };

  // Opening modal to enter sponsor code
  const openModal = () => {
    setSponsorCode("");
    navigate({ pathname: "/events/EnterSponsorCode" });
    setAddSponsorCodeModal(true);
    setError("");
  };

  const clearSearch = () => {
    setSearchValue("");
    if (allEventItems) {
      setEventStorageData(allEventItems);
    }
  };

  const shareAccessCode = (sponsorCode) => {
    localStorage.setItem("sponsorCode", sponsorCode);
    setEventSponsorCode(sponsorCode);
    navigate({ pathname: `/events/ShareSponsorCode` });
  };

  const onCloseModal = () => {
    setAddedEventModal(false);
    try {
      listAllEvents();
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const accessEventClickHandler = () => {
    navigate({
      pathname: `/events/${addedEventDatails.eventId}/scanDetails`,
      search: `?sponsor=${addedEventDatails.sponsorCode}`,
    });
    setAddedEventModal(false);
  };

  // Get user info for avatar
  const userName = Context?.state?.user?.attributes?.name || "";
  const userPicture = Context?.state?.user?.attributes?.picture || "";

  // Mobile Header
  const renderMobileHeader = () => (
    <header className="home-header">
      <div className="home-header__logo">Sponsor App</div>
      <div className="home-header__actions">
        <button
          className="home-header__theme-btn"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          <Icon
            icon={theme === "dark" ? "mdi:weather-sunny" : "mdi:weather-night"}
            width="24"
            height="24"
            color="currentColor"
            style={{ display: 'block' }}
          />
        </button>
        <Avatar
          src={userPicture}
          name={userName}
          size={36}
          onClick={() => setProfileModalOpen(true)}
          className="home-header__avatar"
        />
      </div>
    </header>
  );

  // Search and Add Event Toolbar - render function (not a component)
  const renderToolbar = (className = "") => (
    <div className={`home-toolbar ${className}`}>
      <div className={`home-search ${isDesktop ? "home-search--desktop" : ""}`}>
        <Icon
          icon="proicons:search"
          width="18"
          height="18"
          className="home-search__icon"
        />
        <input
          type="text"
          className="home-search__input"
          placeholder="Search"
          value={searchValue}
          onChange={(e) => filteringData(e.target.value)}
        />
        {searchValue && (
          <button
            className="home-search__clear"
            onClick={() => filteringData("")}
            aria-label="Clear search"
            type="button"
          >
            <Icon icon="iconamoon:close" width="18" height="18" color="currentColor" style={{ display: 'block' }} />
          </button>
        )}
      </div>
      <button className="home-add-btn" onClick={openModal}>
        <Icon icon="icon-park-outline:add-one" width="18" height="18" />
        Add Event
      </button>
    </div>
  );

  // Event List - render function (not a component to avoid remounting)
  const renderEventList = () => {
    if (!eventStorageData) {
      return (
        <div className="home-events__loading">
          <div className="home-spinner" />
        </div>
      );
    }

    if (eventStorageData.length === 0) {
      return <div className="home-events__empty">No Events Found</div>;
    }

    return (
      <div className="home-events__list">
        {eventStorageData.map((event) => (
          <EventCard
            key={event.eventId}
            eventName={event.eventName}
            eventPoster={event.eventPoster}
            eventStartTimeStamp={DateTimeConversion(
              event.startDate,
              event.startTime
            )}
            eventEndTimeStamp={DateTimeConversion(event.endDate, event.endTime)}
            sponsorCode={event.sponsorCode}
            countLeads={event.leadsCount || 0}
            isDownloading={loading === event.sponsorCode}
            disableDownload={event.leadsCount == 0}
            onClickCard={() => {
              navigate({
                pathname: `/events/${event.eventId}/scanDetails`,
                search: `?sponsor=${event.sponsorCode}`,
              });
            }}
            onClickTeam={() => {
              navigate({
                pathname: `/events/${event.eventId}/team`,
                search: `?sponsor=${event.sponsorCode}`,
              });
            }}
            onClickScan={() => {
              navigate({
                pathname: `/events/${event.eventId}/scanDetails`,
                search: `?sponsor=${event.sponsorCode}`,
              });
            }}
            onClickLeads={() => {
              navigate({
                pathname: `/events/${event.eventId}/view-leads`,
                search: `?sponsor=${event.sponsorCode}`,
              });
            }}
            onClickDownload={() => Download(event.sponsorCode, event.eventId)}
            onClickShare={() => shareAccessCode(event.sponsorCode)}
          />
        ))}
      </div>
    );
  };

  // Desktop Layout
  if (isDesktop) {
    return (
      <div className="home-page">
        <AppHeader onProfileClick={() => setProfileModalOpen(true)} />
        <div className="home-content">
          
          <main className="home-main">
            <div className="home-events">
              <div className="home-events__panel">
                <div className="home-events__panel-header">
                  <h2 className="home-events__panel-title">Sponsored Events</h2>
                  {renderToolbar("home-toolbar--desktop")}
                </div>
                {renderEventList()}
              </div>
            </div>
          </main>
        </div>

        <AddedEventDisplayModal
          addedEventModal={addedEventModal}
          setAddedEventModal={setAddedEventModal}
          onCloseModal={onCloseModal}
          accessEventClickHandler={accessEventClickHandler}
          addedEventDatails={addedEventDatails}
        />

        <ProfileModal
          isOpen={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
        />

        <InvalidSponsorCodeModal
          showModal={invalidCodeModalOpen}
          setModal={setInvalidCodeModalOpen}
          onCloseModal={() => setInvalidCodeModalOpen(false)}
        />

        <Outlet
          context={{
            sponsorCode,
            setSponsorCode,
            loading,
            submitClickHandler,
            error: error,
            setError,
            eventSponsorCode,
            addSponsorCodeModal,
            setAddSponsorCodeModal,
          }}
        />
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="home-page">
      {renderMobileHeader()}
      {renderToolbar()}
      <div className="home-events">
        {renderEventList()}
      </div>

      <AddedEventDisplayModal
        addedEventModal={addedEventModal}
        setAddedEventModal={setAddedEventModal}
        onCloseModal={onCloseModal}
        accessEventClickHandler={accessEventClickHandler}
        addedEventDatails={addedEventDatails}
      />

      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      <InvalidSponsorCodeModal
        showModal={invalidCodeModalOpen}
        setModal={setInvalidCodeModalOpen}
        onCloseModal={() => setInvalidCodeModalOpen(false)}
      />

      <Outlet
        context={{
          sponsorCode,
          setSponsorCode,
          loading,
          submitClickHandler,
          error: error,
          setError,
          eventSponsorCode,
          addSponsorCodeModal,
          setAddSponsorCodeModal,
        }}
      />
    </div>
  );
};

export default AllEvents;
